import { FunctionComponent, useEffect, useState } from "react";

import useLocalStorageState from "use-local-storage-state";

import { useNavigate } from "react-router-dom";

import { useTranslation } from "react-i18next";

import { ChevronLeft, Search } from "lucide-react";

import { isIPv4 } from "is-ip";

import { classNames } from "@/utils/classNames";

import { ROUTE_CONFIG } from "@/routes/config";

import { SidenavItem, useNavigationGetter } from "@/constants/sidenav.config";

import { useHasPermissions } from "@/utils/useHasPermissions";

import { setAuth } from "@/pages/Public/redux/auth.slice";

import { useAppDispatch } from "@/redux/hooks";

import { useLocationMutation } from "@/pages/Private/redux/account/account.api";

import { useGetFeaturesQuery } from "@/pages/Private/redux/feature/feature.api";

import { clearLists } from "@/pages/Private/redux/list/list.slice";
import { useReconnectEmailMutation } from "@/pages/Private/redux/email/email.api";

import AppLogoFull from "@/assets/images/signalistlogo.png";

import { Button } from "../Button/Button";
import { ButtonColor, ButtonSize } from "../Button/types";
import { GlobalSearchModal } from "../GlobalSearchModal/GlobalSearchModal";
import { Tooltip } from "../Tooltip/Tooltip";
import { useTopbarInfo } from "../Topbar/useTopbarInfo";
import { SidebarConnectionWarnings } from "./SidebarConnectionWarnings";
import { SidebarEmailWarning } from "./SidebarEmailWarning";
import { SidebarSection } from "./SidebarSection";
import { SidebarFooter } from "./SidebarFooter";
import { useSidebarCounts } from "./useSidebarCounts";
import { useLeadScoringStatus } from "./useLeadScoringStatus";
import { resolveReviewBubble, useAiReviewStatus } from "./useAiReviewStatus";

const { DASHBOARD } = ROUTE_CONFIG;

export const Sidebar: FunctionComponent = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { t } = useTranslation();
	const ts = (key: string, opts?: Record<string, unknown>) => t(`sidebar.${key}`, opts);

	const [showSearchModal, setShowSearchModal] = useState(false);

	const [isCollapsed, setIsCollapsed] = useLocalStorageState("isSidebarCollapsed", {
		defaultValue: false,
	});
	const isExpanded = !isCollapsed;

	const { data: features } = useGetFeaturesQuery();
	const { hasPermissions } = useHasPermissions();

	const {
		initialUser,
		isLinkedinNotConnected,
		isSalesNavigatorNotConnected,
		processingLists,
		user,
		loggedOutEmails,
	} = useTopbarInfo();

	const [reconnectEmail] = useReconnectEmailMutation();
	const [updateLocation] = useLocationMutation();

	const counts = useSidebarCounts(user?.id);
	const { isScoreDone, isScoreDoneText } = useLeadScoringStatus();
	const aiReviewStatus = useAiReviewStatus();

	const NavItemConfig = useNavigationGetter();
	const items = NavItemConfig.ALL;

	// Global keyboard shortcuts: ⌘/Ctrl+K opens search, "[" toggles the sidebar.
	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			const target = e.target as HTMLElement;
			const isTyping = /input|textarea|select/i.test(target.tagName) || target.isContentEditable;

			if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
				e.preventDefault();
				setShowSearchModal(true);

				return;
			}

			if (e.key === "[" && !isTyping) {
				setIsCollapsed((collapsed) => !collapsed);
			}
		};

		window.addEventListener("keydown", handler);

		return () => window.removeEventListener("keydown", handler);
	}, [setIsCollapsed]);

	useEffect(() => {
		if (!user?.city || !user?.country || !user?.isp || !user?.region || !user?.timezone) {
			fetch("https://api.ipify.org?format=json")
				.then((res) => res.json())
				.then((data) => {
					if (isIPv4(data.ip)) {
						updateLocation(data);
					}
				});
		}
	}, [updateLocation, user?.city, user?.country, user?.isp, user?.region, user?.timezone]);

	const handleReconnectEmail = async (emailId: number) => {
		try {
			const response = await reconnectEmail({
				id: emailId,
				successRedirectUrl: window.location.href || undefined,
			}).unwrap();

			if (response.url) {
				window.open(response.url, "_blank");
			}
		} catch (error) {
			console.error(error);
		}
	};

	const resetInitialUser = () => {
		dispatch(setAuth(initialUser));
		dispatch(clearLists());
		localStorage.removeItem("initialUser");
		navigate(ROUTE_CONFIG.LISTS);
	};

	const filterItem = (item: SidenavItem): boolean => {
		if (item.permission && !hasPermissions([item.permission])) {
			return false;
		}

		if (item.companyIds && user?.company && !item.companyIds.includes(user.company.id)) {
			return false;
		}

		if (item.feature) {
			return !!features?.find((f) => f.feature === item.feature)?.active;
		}

		if (item.notFeature) {
			return features?.find((f) => f.feature === item.notFeature)?.active === false;
		}

		return true;
	};

	const enrich = (item: SidenavItem) => {
		const result: {
			count?: number;
			bubbleColor?: "red" | "yellow" | "green" | "orange";
			bubbleText?: React.ReactNode;
		} = {};

		if (item.path && counts[item.path] !== undefined) {
			result.count = counts[item.path];
		}

		if (item.path === ROUTE_CONFIG.LEAD_SCORING) {
			result.bubbleColor = isScoreDone;
			result.bubbleText = isScoreDoneText;
		}

		// Orange "needs review" dot for AI-generated / unreviewed sections. The
		// Knowledge DB dot only appears once Lead Scoring is green (PREC-3631).
		const reviewBubble = resolveReviewBubble(item.path, aiReviewStatus, isScoreDone === "green");

		if (reviewBubble) {
			result.bubbleColor = reviewBubble.bubbleColor;
			result.bubbleText = reviewBubble.bubbleText;
		}

		return result;
	};

	const hasConnectionWarnings =
		isLinkedinNotConnected ||
		isSalesNavigatorNotConnected ||
		processingLists.length > 0 ||
		!!initialUser ||
		(loggedOutEmails?.length ?? 0) > 0;

	return (
		<aside
			className={classNames(
				"z-[200] flex-shrink-0 flex flex-col h-screen border-r border-gray-200 overflow-x-hidden transition-[width] duration-200 print:hidden",
				initialUser ? "bg-other-org" : "bg-brand-white",
				isExpanded ? "w-[248px]" : "w-[68px]"
			)}
		>
			{/* BRAND */}
			<div
				className={classNames(
					"h-[60px] flex items-center flex-shrink-0",
					isExpanded ? "px-[18px] justify-between" : "justify-center"
				)}
			>
				{isExpanded && (
					<img
						alt="signalist"
						className="h-[22px] w-auto cursor-pointer 13i:max-w-[210px]"
						src={AppLogoFull}
						onClick={() => navigate(DASHBOARD)}
					/>
				)}
				<Tooltip
					content={isExpanded ? ts("collapseSidebar") : ts("expandSidebar")}
					direction="top-right"
					variant="dark"
				>
					<button
						aria-label={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
						className="w-7 h-7 flex items-center justify-center rounded-[7px] text-placeholder hover:bg-sidebar-item-hover hover:text-text-meta cursor-pointer transition-colors"
						onClick={() => setIsCollapsed((collapsed) => !collapsed)}
					>
						<ChevronLeft
							className={classNames("w-5 h-5 transition-transform", isExpanded ? "" : "rotate-180")}
							strokeWidth={2}
						/>
					</button>
				</Tooltip>
			</div>

			{/* CTA */}
			<div className="px-3 pt-1 pb-2 flex-shrink-0">
				<Button
					className="w-full"
					color={ButtonColor.ACTION}
					size={ButtonSize.S}
					title={isExpanded ? "Create new list" : "+"}
					onClick={() => navigate(ROUTE_CONFIG.NEW_LIST)}
				/>
			</div>

			{/* SEARCH */}
			<div className="px-3 pb-2 flex-shrink-0">
				<div
					className={classNames(
						"flex items-center h-9 rounded-[9px] border border-gray-200 bg-white cursor-pointer transition-colors hover:border-brand-light",
						isExpanded ? "gap-2 px-[11px]" : "justify-center w-11 mx-auto"
					)}
					onClick={() => setShowSearchModal(true)}
				>
					<Search className="w-[15px] h-[15px] text-placeholder flex-shrink-0" strokeWidth={2} />
					{isExpanded && (
						<>
							<span className="flex-1 text-sm text-placeholder whitespace-nowrap">
								{ts("globalSearch")}
							</span>
							<kbd className="text-[11px] text-placeholder bg-brand-white border border-gray-200 rounded px-1.5 py-0.5">
								⌘K
							</kbd>
						</>
					)}
				</div>
			</div>

			{/* CONNECTION WARNINGS */}
			{hasConnectionWarnings && (
				<div
					className={classNames(
						"px-3 pb-2 flex-shrink-0 flex gap-2",
						isExpanded ? "flex-col" : "flex-col items-center"
					)}
				>
					<SidebarConnectionWarnings
						collapsed={!isExpanded}
						isLinkedinNotConnected={isLinkedinNotConnected}
						isSalesNavigatorNotConnected={isSalesNavigatorNotConnected}
					/>

					{processingLists.length > 0 &&
						(isExpanded ? (
							<Tooltip content={processingLists.map((l) => l.name).join(", ")} direction="bottom">
								<div className="text-sm text-white bg-text-meta rounded-full px-2 py-1 whitespace-nowrap">
									{processingLists.length} {ts("listsAreBeingProcessed")}
								</div>
							</Tooltip>
						) : (
							<Tooltip content={`${processingLists.length} / 2 lists are being processed`}>
								<div className="w-6 h-6 rounded-full bg-text-meta flex items-center justify-center text-xxs font-bold text-white">
									{processingLists.length}
								</div>
							</Tooltip>
						))}

					<SidebarEmailWarning
						collapsed={!isExpanded}
						emails={loggedOutEmails ?? []}
						onReconnect={handleReconnectEmail}
					/>

					{initialUser && isExpanded && (
						<div className="text-sm">
							{ts("loggedInAs", {
								firstName: initialUser?.user?.firstName,
								lastName: initialUser?.user?.lastName,
								asFirstName: user?.firstName,
								asLastName: user?.lastName,
							})}
							<b>{user?.company?.name}</b>
						</div>
					)}
				</div>
			)}

			{/* NAV */}
			<nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-1 scrollbar-hide">
				<SidebarSection
					filterItem={filterItem}
					isExpanded={isExpanded}
					items={items.dashboard}
					label={ts("dashboard")}
				/>
				<SidebarSection
					enrich={enrich}
					filterItem={filterItem}
					isExpanded={isExpanded}
					items={items.messages}
					label="Messages"
				/>
				<SidebarSection
					enrich={enrich}
					filterItem={filterItem}
					isExpanded={isExpanded}
					items={items.signalsLeads}
					label="Signals & Leads"
				/>
				<SidebarSection
					enrich={enrich}
					filterItem={filterItem}
					isExpanded={isExpanded}
					items={items.configurations}
					label="Configurations & Analytics"
				/>
				<SidebarSection
					enrich={enrich}
					filterItem={filterItem}
					isExpanded={isExpanded}
					items={items.help}
					label="Help"
				/>
				<SidebarSection
					enrich={enrich}
					filterItem={filterItem}
					isExpanded={isExpanded}
					items={items.lower}
					label={ts("settings")}
				/>
			</nav>

			{/* FOOTER */}
			<SidebarFooter
				initialUser={initialUser}
				isExpanded={isExpanded}
				onResetInitialUser={resetInitialUser}
				user={user}
			/>

			{showSearchModal && (
				<GlobalSearchModal isOpened={showSearchModal} setIsOpened={setShowSearchModal} />
			)}
		</aside>
	);
};
